import { ottActionFilter } from '../../api/models/ActionFilter';
import { NavigationApi } from '../../api/NavigationApi';
import { RibbonDataSource } from './RibbonDataSource';
import { GridDataSource } from './GridDataSource';
import { BannerDataSource } from './BannerDataSource';
import { Log } from '@lightningjs/sdk';
import { Backend } from '../../api/Backend';
import { ChannelModel } from '../../api/EpgApi.types';
import { DataSource } from './DataSource';
import { ICustomCmsRow, ICustomPage } from '../../api/models/AndromeTV.types';
import { CustomPageParam } from '../../api/CmsApi.types';

const _constants = {
  initialLoad: 3,
  loadNext: 2,
};

export class PageDataSource {
  private backend: Backend;
  private pageId: string;
  private channels: ChannelModel[];
  private activePromis: Promise<DataSource[]> | undefined;
  private dataSources: DataSource[] = [];
  private loadedIdx = 0;
  private title: string | undefined;
  private background: string | undefined;
  private properties?: string[];
  private tags?: string[];
  constructor(backend: Backend, pageId: string, channels: ChannelModel[]) {
    this.backend = backend;
    this.pageId = pageId;
    this.channels = channels;
  }

  /**
   * Load the page and initial datasource entries
   * @returns {Promise<DataSource[]>}
   */
  load(): Promise<DataSource[]> {
    const customParam: CustomPageParam = {
      entitlementId: this.backend.sessionInfo?.device.entitlementId
        ? this.backend.sessionInfo.device.entitlementId
        : '',
      actionFilter: ottActionFilter,
      adult: false,
    };
    if (this.activePromis) {
      // load next
      return this.activePromis.then(() => {
        const ds = this.dataSources.slice(this.loadedIdx, this.loadedIdx + _constants.loadNext);
        this.loadedIdx += _constants.loadNext;
        return Promise.all(ds.map(x => x.load()))
          .then(data => {
            return data.filter(x => x && !x.isEmpty());
          })
          .catch(err => {
            Log.error('Failed to load next page data', err);
            return [];
          });
      });
    } else {
      // initial load
      this.activePromis = NavigationApi.customPage(this.backend, this.pageId, customParam).then(data => {
        // console.log('Page data', data);
        this.title = data.title;
        this.background = data.backgroundImage;
        this.properties = data.cmsPage?.properties;
        this.tags = data.cmsPage?.categoryTags;

        if (data.cmsPage && data.cmsPage.rows) {
          const newData = data.cmsPage.rows.map(row => this.dataSourceForRow(row, data)).filter(x => x);
          if (newData) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this.dataSources = newData.filter(el => el !== undefined);
          }
        }
        const ds = this.dataSources.slice(0, _constants.initialLoad);
        this.loadedIdx = _constants.initialLoad;
        return Promise.all(ds.map(x => x.load()))
          .then(data => {
            return data.filter(x => x && !x.isEmpty());
          })
          .catch(err => {
            Log.error('Failed to load initial page data', err);
            return [];
          });
      });
      return this.activePromis;
    }
  }

  dataSourceForRow(row: ICustomCmsRow, data: ICustomPage) {
    let props: string[] = [];
    if (data.cmsPage && data.cmsPage.properties) {
      props = data.cmsPage.properties;
    }
    switch (row.type) {
      case 'RIBBON':
        return row.ribbon ? new RibbonDataSource(this.backend, row.ribbon, props, this.channels) : undefined;
      case 'GRID':
        return row.grid ? new GridDataSource(this.backend, row.grid, props, this.channels) : undefined;
      case 'BANNER':
        return row.banners ? new BannerDataSource(this.backend, row.banners, props) : undefined;
      default:
        Log.warn(`Unsupported CMS Page row type ${row.type}`);
        return undefined;
    }
  }
}
